<template>
    <div>
        <div
            v-observe-visibility="visibilityChanged"
            class="account__steps"
        >
            <div v-if="steps.active === 5" class="account__status">
                <span>Complete</span>
            </div>
            <Steps v-else :current="steps.active" :total="steps.total" @changeStep="change" />
        </div>
        <component
            :is="currentView"
            id="commissioning-sheet__main"
            :job="job.id"
            :installer-name="installerName"
            :designer-name="designerName"
            :commissioning-sheet.sync="commissioningSheet"
            :job-object="job"
            :details="job.details"
            :steps-valid="stepsValid"
            :show-helper="isSectionVisible"
            @saveExit="saveExit"
            @saveNext="saveNext"
            @validateStep="validateStep"
        />
        <div v-show="loading" class="loading-state mt-md">
            <img src="/dist/images/loader.gif" alt="Loading">
        </div>
        <div v-show="error">
            <p class="small semi text--orange ml-lg">{{ error }}</p>
        </div>
    </div>
</template>
<script>
/* eslint-disable no-console */
import { axios } from '@/../utils';
import { get, round, find } from 'lodash';
import Steps from '@/jobs/new/Steps';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';

export default {
    components: {
        Steps,
        Step2,
        Step3,
        Step4,
        Step5,
        Step1
    },

    data() {
        return {
            commissioningSheet: {},
            mounted: false,
            loading: false,
            error: false,
            job: JSON.parse(this.$root.job),
            cableDiagram: JSON.parse(this.$root.cableDiagram),
            products: JSON.parse(this.$root.products),
            configuration: JSON.parse(this.$root.configuration),
            user: JSON.parse(this.$root.user),
            installer: JSON.parse(this.$root.installer),
            steps: {
                active: 1,
                total: 4
            },
            stepsValid: [false, false, false],
            isSectionVisible: false
        };
    },

    computed: {
        currentView() {
            return `step${this.steps.active}`;
        },
        installerName() {
            return this.job.details.additionalRole && this.job.details.additionalRole === 'Installer' ? this.job.details.additionalName : this.user.firstName + ' ' + this.user.lastName;
        },
        installerNumber() {
            return this.job.details.additionalRole && this.job.details.additionalRole === 'Installer' ? this.job.details.additionalNumber : this.job.details.accreditationNumber;
        },
        designerName() {
            return this.job.details.additionalRole && this.job.details.additionalRole === 'Designer' ? this.job.details.additionalName : this.user.firstName + ' ' + this.user.lastName;
        },
        designerNumber() {
            return this.job.details.additionalRole && this.job.details.additionalRole === 'Designer' ? this.job.details.additionalNumber : this.job.details.accreditationNumber;
        },
        temperature() {
            return get(this.job, 'details.temperature.min', NaN);
        },

        // step 1 data
        generalData() {
            const dataObject = {};

            dataObject.installerName = this.installerName;
            dataObject.installerNumber = this.installerNumber;
            dataObject.designerName = this.designerName;
            dataObject.designerNumber = this.designerNumber;
            dataObject.electricianName = '';
            dataObject.electricianNumber = '';
            dataObject.name = this.job.details.name;
            dataObject.email = this.job.details.email;
            dataObject.address = this.job.details.address.string;
            dataObject.buildingType = this.job.details.buildingType;
            dataObject.phone = this.job.details.phone ? this.job.details.phone :  '';
            dataObject.jobNumber = this.job.details.jobNumber ? this.job.details.jobNumber : '';
            dataObject.nmi = this.job.details.nmi ? this.job.details.nmi : '';
            dataObject.meterNumber = this.job.details.meterNumber ? this.job.details.meterNumber : '';
            dataObject.noOfPhases = this.job.details.noOfPhases ? this.job.details.noOfPhases : '';
            dataObject.installationDate = this.job.details.installationDate ? this.job.details.installationDate : '';
            dataObject.dnsp = '';
            dataObject.energyRetailer = '';
            dataObject.networkRef = '';
            dataObject.exportLimitationRequired = 'No';
            dataObject.limitedTo = '';

            return dataObject;
        },

        // step 2 data
        invertersData() {
            const dataArray = [];

            this.products.inverters.inverters.map((inverter, index) => {

                let pvamv = 0;
                // Mppts Strings
                const strings = [];
                this.configuration[index].mppts.map(mppt => {
                    [...Array(Number(mppt.parallel))].map(() => {
                        strings.push({
                            mppt: 'MPPT' + mppt.id,
                            panels: Number(mppt.series),
                            azimuth: Number(mppt.spe.azimuth),
                            tilt: Number(mppt.spe.tilt),
                        })
                    })

                    const panel = this.products.pvModules.panels.filter(panel => panel.id === mppt.panel.id)[0];
                    const panelNumbers = Number(mppt.series);

                    const pvamvMppt = this.pvamv(panelNumbers, panel);
                    if (pvamv < pvamvMppt) {
                        pvamv = pvamvMppt;
                    }
                })

                const iscCalc = this.configuration[0].mppts.reduce((acc, mppt) => acc + this.isc(mppt), 0);
                const isc = iscCalc.toFixed(2);

                // Get the right PV Model
                const panel = this.products.pvModules.panels.filter(panel => panel.id === this.configuration[index].mppts[0].panel.id)[0];

                // Get the right PV Model
                const voltageRiseData = this.cableDiagram && this.cableDiagram.voltageRiseData ? this.cableDiagram.voltageRiseData[index] : '';

                dataArray.push({
                    heading: 'Inverter' + ' ' + Number(index + 1),
                    productManufacturer: inverter.details.productManufacturer,
                    productModelNumber: inverter.details.productModelNumber,
                    maxDCWatts: inverter.details.maxDCWatts,
                    numberOfMPPTrackers: inverter.details.numberOfMPPTrackers,
                    maxShortCircuitCurrent: inverter.details.maxShortCircuitCurrent,
                    maxInputCurrent: inverter.details.maxInputCurrent,
                    inverterInstalledToInstructions: false,
                    acIsolatorCorrect: false,
                    acCircuitCorrect: false,
                    acCableVoltageWithinLimit: false,
                    acCable: '',
                    acVRiseLoad: '',
                    acVRiseNoLoad: '',
                    panelManufacturer: panel.manufacturer,
                    panelModel: panel.model,
                    panelDCManifacturer: panel.details.connectorManufacturer,
                    panelDCModel: panel.details.connectorType,
                    stringFuseVolt: '',
                    stringFuseCurrent: '',
                    pvMaxVolt: pvamv,
                    pvShortCircuit: isc,
                    dcIsolatorsNum: 0,
                    dcConfirmation: false,
                    inverterHasDcIsolator: false,
                    dcIsolatorAusStandard: false,
                    dcIsolatorSpecProvided: false,
                    dcIsolatorDeclarationProvided: false,
                    separatedDcIsolatorRequired: null,
                    dcIsolatorEnclosuresIpMaintained: false,
                    dcIsolatorManifacturerInstructions: false,
                    dcIsolatorTestedLoad: false,
                    conduitCompliant: false,
                    roofPenetrationSealed: false,
                    cableProtected: false,
                    cableVdInLimit: false,
                    dcCableVd: '',
                    mountingSystemManifacturer: '',
                    mountingSystemModel: '',
                    systemManifacturerInstructions: false,
                    systemRoofPenetrationSealed: false,
                    systemZones: false,
                    systemArrayFrame: false,
                    systemNoContactMetals: false,
                    systemPanelManifacturerInstructions: false,
                    systemPanelManifacturerZones: false,
                    systemBolts: false,
                    systemEarthed: false,
                    systemEarthConnection: false,
                    mountingEarthFaultAlarm: '',
                    systemRequirement3000: false,
                    systemRequirement5033: false,
                    systemRequirement4777: false,
                    systemRequirementCEC: false,
                    LVbyLicensed: false,
                    LVtestedbyLicensed: false,
                    systemCompliant: false,
                    DERdocumented: false,
                    systemLabelled: false,
                    inverterRemoteMonitoring: false,
                    voltVarV: ['', '', '', ''],
                    voltVarPercentage: ['', '', '', ''],
                    voltWattV: ['', '', '', ''],
                    voltWattPercentage: ['', '', '', ''],
                    inverterExportLimited: 'No',
                    exportLimitSet: '',
                    inverterInstalledNSP: false,
                    countryCodeSet: false,
                    inverterShutdownSeconds: false,
                    inverterStartSeconds: false,
                    inverterRampSeconds: false,
                    voltageRiseData: voltageRiseData,
                    strings: strings
                });

                // Get data from Voltage Rise
                if (this.cableDiagram && this.cableDiagram.distribution) {
                    const limitationDevice = this.cableDiagram.distribution[index].limitationDevice;
                    const limitedTo = limitationDevice ? limitationDevice.limit : null;
                    const limitationDeviceLocation = limitationDevice ? limitationDevice.location : null;

                    if (limitedTo) {
                        dataArray[index].limitedTo = limitedTo;
                    }

                    if (limitationDeviceLocation) {
                        dataArray[index].limitationDeviceLocation = limitationDeviceLocation;
                    }
                }
            })

            return dataArray;
        },

        // step 3 data
        invertersResultData() {
            const dataArray = [];
            this.products.inverters.inverters.map((inverter, index) => {

                const mppts = [];

                this.configuration[index].mppts.map((mppt, index) => {
                    // Get number of strings
                    const mpptString = Number(mppt.parallel);
                    // Get the right PV Model
                    const panel = this.products.pvModules.panels.filter(panel => panel.id === mppt.panel.id)[0];
                    // Get number of panels
                    const panelNumbers = Number(mppt.series);

                    const pvModelVoc = panel.details.voc * panelNumbers;
                    const pvModelIscSum = panel.details.panelIsc * mpptString;
                    const pvModelIsc = panel.details.panelIsc;

                    const pvamvMppt = this.pvamv(panelNumbers, panel);

                    if (mpptString > 1) {
                        const totalStrings = Array(mpptString).fill().map((_, i) => i + 1).join(' + ');

                        mppts.push({
                            label: 'MPPT' + (index + 1) + ': String ' + totalStrings,
                            pvModelVoc: pvModelVoc.toFixed(2),
                            pvModelIsc: pvModelIscSum.toFixed(2),
                            pvamv: pvamvMppt,
                            correctPolarity: false,
                            earthContinuity: '',
                            insulationResistanceMinus: '',
                            insulationResistancePlus: '',
                            measuredVoc: '',
                            measuredIsc: '',
                            measuredIrrad: '',
                            measuredVmp: '',
                            measuredImp: '',
                        });
                    }

                    [...Array(mpptString)].map((_,i) => {
                        mppts.push({
                            label: 'MPPT' + (index + 1) + ': String ' + (i + 1),
                            pvModelVoc: pvModelVoc.toFixed(2),
                            pvModelIsc: pvModelIsc.toFixed(2),
                            pvamv: pvamvMppt,
                            correctPolarity: false,
                            earthContinuity: '',
                            insulationResistanceMinus: '',
                            insulationResistancePlus: '',
                            measuredVoc: '',
                            measuredIsc: '',
                            measuredIrrad: '',
                            measuredVmp: '',
                            measuredImp: '',
                        })
                    })
                })

                dataArray.push({
                    heading: 'Inverter Result' + ' ' + Number(index + 1),
                    mppts: mppts,
                });
            })

            return dataArray;
        },

        // step 4 data
        finaliseData() {
            const dataObject = {};
            dataObject.reviewed = false;
            dataObject.installerAttended = false;
            dataObject.installerSystemInstallation = false;
            dataObject.designerAttended = false;
            dataObject.designerSystemInstallation = false;

            return dataObject;
        }
    },

    created() {
        if (this.job.details.commissioningSheet) {
            this.commissioningSheet = this.job.details.commissioningSheet;

            if (this.job.details.commissioningSheet.completed == 'true') {
                this.change(5);
            }

        } else {
            this.commissioningSheet = {
                general: this.generalData,
                inverters: this.invertersData,
                invertersResult: this.invertersResultData,
                finalise: this.finaliseData,
                completed: false
            }
        }
    },

    methods: {
        change(step) {
            this.steps.active = step;

            window.scrollTo(0, 0);
        },

        saveExit() {
            this.submit('exit');
        },

        saveNext(step) {
            this.submit(step);
        },

        visibilityChanged (isVisible) {
            this.isSectionVisible = !isVisible;
        },

        pvamv(series, panel) {
            const { details: { voc, vDeratingCelsius } } = panel;
            const pvMaxVoltage = Number(voc) + ((this.temperature - 25)  * (Number(voc) * Number(vDeratingCelsius) / 100));
            return Math.round(pvMaxVoltage * series);
        },

        isc(mppt) {
            const { details: { panelIsc } } = find(this.products.pvModules.panels, ['id', mppt.panel.id]);
            return round(panelIsc * mppt.parallel, 2);
        },

        validateStep(step, value) {
            this.stepsValid[step] = value;
        },

        async submit(action) {
            this.loading = true;

            const commissioningSheetCompleted = {...this.commissioningSheet, completed: true};

            // If last step completed set completed true
            const commissioningSheet = action === 5 ? commissioningSheetCompleted : this.commissioningSheet;

            try {
                const response = await axios.post(`/account/job/${this.job.id}/CommissioningSheet/datasheet`, commissioningSheet);

                console.log(response.data);

                if (response.data.success) {
                    this.loading = false;

                    if (action === 'exit') {
                        window.location = `/account/job/${this.job.id}`

                    } else {
                        this.change(action);
                    }
                }

            } catch (error) {
                this.loading = false;
                this.error = 'A connection issues has occured. Please try again later.';

                console.log(error)
            }
        },
    }
}
</script>
